import {
  CarrierCostType,
  ProfitType,
} from '../pages/QuoteView/Quoting/RequestQuickQuote/types';

// FIXME: Not handling line haul correctly.
export const calculateFlatCarrierCost = (
  carrierCost: number,
  carrierCostType: CarrierCostType,
  distance: number
): number => {
  return carrierCostType === CarrierCostType.Flat
    ? carrierCost
    : carrierCost * distance;
};

const calculateFinalProfit = (
  profit: number,
  profitType: ProfitType,
  carrierCostType: CarrierCostType,
  flatCarrierCost: number
): number => {
  return profitType === ProfitType.Percentage ||
    carrierCostType === CarrierCostType.Flat
    ? profit // Percentage profit or flat cost stays as-is
    : profit * flatCarrierCost; // Convert to flat profit $ value
};

export const calculatePricing = (
  carrierCost: number,
  carrierCostType: CarrierCostType,
  profit: number,
  profitType: ProfitType,
  distance: number
): { flatCarrierCost: number; finalProfit: number } => {
  const flatCarrierCost = calculateFlatCarrierCost(
    carrierCost,
    carrierCostType,
    distance
  );
  const finalProfit = calculateFinalProfit(
    profit,
    profitType,
    carrierCostType,
    flatCarrierCost
  );

  return { flatCarrierCost, finalProfit };
};

export interface CalculateProfitFromFinalPriceParams {
  finalPrice: number;
  carrierCost: number;
  profitType: ProfitType;
  isMarginEnabled: boolean;
}

/**
 * Back-calculate margin/markup from a sell (bill) rate and carrier cost.
 * `finalPrice` should be the quote-unit sell before accessorial add-ins.
 */
export const calculateProfitFromFinalPrice = ({
  finalPrice,
  carrierCost,
  profitType,
  isMarginEnabled,
}: CalculateProfitFromFinalPriceParams): number => {
  if (profitType === ProfitType.Amount) {
    return finalPrice - carrierCost;
  }

  if (carrierCost <= 0 || finalPrice <= 0) {
    return 0;
  }

  if (isMarginEnabled) {
    if (finalPrice === carrierCost) {
      return 0;
    }

    return (1 - carrierCost / finalPrice) * 100;
  }

  return (finalPrice / carrierCost - 1) * 100;
};

/** Trip-level sell total (flat $) from CarrierPriceCalculator sell in quote units. */
export const tripTotalFromSellQuoteUnits = (
  sellQuoteUnits: number,
  carrierCostType: CarrierCostType,
  distance: number
): number => {
  if (carrierCostType === CarrierCostType.Flat) {
    return sellQuoteUnits;
  }
  return distance > 0 ? sellQuoteUnits * distance : sellQuoteUnits;
};

/** Sell in quote units ($/mi or flat) for CarrierPriceCalculator from a trip total. */
export const sellQuoteUnitsFromTripTotal = (
  tripTotal: number,
  carrierCostType: CarrierCostType,
  distance: number
): number => {
  if (carrierCostType === CarrierCostType.Flat) {
    return tripTotal;
  }
  return distance > 0 ? tripTotal / distance : tripTotal;
};
